import React from 'react';
import styled from 'styled-components';

/**
 * Shared "how Thea works" device treatment: a framed-iPhone screenshot
 * (uniform status bar + top-75% crop) plus the `FeatureBeatsFrame` shape
 * that describes one frame. Consumed by FeatureStack (the homepage stack)
 * and homepageStackFrames. The rotating carousel that used to live here
 * was retired when FeatureStack became the default beats section (PR #294);
 * BeatDevice + the frame type are what remain.
 */

/* ---------------------------------------------------------------- device */

const Phone = styled.div<{ $w: number; $wDesktop: number }>`
  width: 100%;
  /* Bigger / more prominent on mobile (Kate 2026-06-18) — wider + taller.
     Desktop keeps the smaller size so it fits its grid column. Widths are
     props so FeatureStack can render the same device larger (defaults =
     BeatDevice's locked sizes). */
  max-width: ${({ $w }) => $w}px;
  margin: 0 auto;
  aspect-ratio: 9 / 19;
  @media (min-width: 768px) {
    max-width: ${({ $wDesktop }) => $wDesktop}px;
  }
  background: #131210;
  border-radius: 36px;
  padding: 8px;
  box-shadow:
    0 18px 40px rgba(61, 53, 48, 0.18),
    0 5px 14px rgba(61, 53, 48, 0.12);
`;

/* Crop the phone to its top ~75% — the chin bleeds past the clip, so we
   never show the empty lower phone. The warm container is BeatCard below
   (wraps the still + its copy as one unit). */
const PhoneClip = styled.div<{ $w: number; $wDesktop: number }>`
  width: 100%;
  max-width: ${({ $w }) => $w}px;
  /* 9 / (19 * 0.75) → show the top ~75% of the phone. */
  aspect-ratio: 9 / 14.25;
  overflow: hidden;
  @media (min-width: 768px) {
    max-width: ${({ $wDesktop }) => $wDesktop}px;
  }
`;

const Screen = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 32px;
  overflow: hidden;
  background: ${({ theme }) => theme.color.creamLight};
  display: flex;
  flex-direction: column;
`;

/* Show the screenshot at its full width (no side-crop / zoom) so the app's
   own side margins read as breathing room. Anchored to the top; the taller
   bottom of the shot is what the phone clips. */
const ScreenImg = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: auto;
  display: block;
`;

/* A clean, consistent iPhone status bar drawn on every frame (Kate 2026-06-19),
   so the whole device reads as a real phone — the screenshots' own bars are
   cropped off, this one is uniform across all frames. */
const StatusBar = styled.div`
  position: relative;
  z-index: 2;
  flex: 0 0 auto;
  height: 26px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 11px 0 14px;
`;
const SbTime = styled.span`
  font-size: 10.5px;
  font-weight: 700;
  color: #000;
  letter-spacing: 0.2px;
`;
const Island = styled.span`
  position: absolute;
  left: 50%;
  top: 5px;
  transform: translateX(-50%);
  width: 44px;
  height: 13px;
  background: #000;
  border-radius: 999px;
`;
const SbIcons = styled.span`
  display: flex;
  align-items: center;
  gap: 3.5px;
`;
const ImageArea = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

const STATUS_BAR = (
  <StatusBar aria-hidden="true">
    <SbTime>9:41</SbTime>
    <Island />
    <SbIcons>
      <svg width="15" height="10" viewBox="0 0 19 13"><g fill="#000"><rect x="0" y="9" width="3" height="4" rx="1" /><rect x="5.3" y="6" width="3" height="7" rx="1" /><rect x="10.6" y="3" width="3" height="10" rx="1" /><rect x="15.9" y="0" width="3" height="13" rx="1" /></g></svg>
      <svg width="14" height="10" viewBox="0 0 18 13" fill="#000"><path d="M9 2.6c2.7 0 5.2 1 7.1 2.8l-1.5 1.6C13.1 5.6 11.1 4.8 9 4.8s-4.1.8-5.6 2.2L1.9 5.4C3.8 3.6 6.3 2.6 9 2.6z" /><path d="M9 6.4c1.5 0 2.9.6 3.9 1.6l-1.5 1.6c-.6-.6-1.5-1-2.4-1s-1.8.4-2.4 1L5.1 8c1-1 2.4-1.6 3.9-1.6z" /><circle cx="9" cy="10.8" r="1.4" /></svg>
      <svg width="21" height="10" viewBox="0 0 27 13"><rect x="0.5" y="0.5" width="23" height="12" rx="3.5" fill="none" stroke="#000" strokeOpacity="0.35" /><rect x="2" y="2" width="17" height="9" rx="1.8" fill="#000" /><rect x="24.5" y="4" width="2.2" height="5" rx="1.1" fill="#000" fillOpacity="0.35" /></svg>
    </SbIcons>
  </StatusBar>
);

/* ------------------------------------------------------------- the device */

export interface FeatureBeatsFrameDeviceProps {
  frame: FeatureBeatsFrame;
  /** Rendered device width caps (px). Defaults are the locked base sizes;
   *  FeatureStack passes larger ones. */
  mobileWidth?: number;
  desktopWidth?: number;
  /** Optional overlay rendered inside ImageArea on top of the screenshot
   *  (e.g. FeatureStack's TypedQuizNote). Additive slot with a no-op
   *  default. */
  screenOverlay?: React.ReactNode;
}

/** The framed-iPhone screenshot (status bar + top-75% crop) for one frame.
 *  Used by FeatureStack's stacked layout. */
export const BeatDevice: React.FC<FeatureBeatsFrameDeviceProps> = ({
  frame,
  mobileWidth = 264,
  desktopWidth = 210,
  screenOverlay,
}) => (
  <PhoneClip $w={mobileWidth} $wDesktop={desktopWidth}>
    <Phone $w={mobileWidth} $wDesktop={desktopWidth}>
      <Screen>
        {STATUS_BAR}
        <ImageArea>
          <ScreenImg
            src={frame.image}
            width={frame.w}
            height={frame.h}
            alt={frame.alt}
          />
          {screenOverlay}
        </ImageArea>
      </Screen>
    </Phone>
  </PhoneClip>
);

/* -------------------------------------------------------------- the frame */

export interface FeatureBeatsFrame {
  image: string;
  /** Intrinsic pixel dimensions of the cropped screenshot. Set as the img's
   *  width/height attributes so the browser reserves the right height BEFORE
   *  the image decodes — otherwise it collapses to the top of the phone while
   *  loading (the "squished up" bug), most visible on mobile + on each swap. */
  w: number;
  h: number;
  alt: string;
  label: string;
  body: string;
  /** Optional freeform note typed INSIDE the screenshot's textarea
   *  (FeatureStack only). Geometry is coupled to beats/add-people.png; see
   *  TypedQuizNote for the motion/a11y rules. */
  typedExample?: string;
}
